import { useMemo, type JSX } from 'react'
import { type TBResource } from '@ttab/textbit'
import {
  Bold,
  Italic,
  Image,
  Text,
  TTVisual,
  Factbox,
  Table,
  LocalizedQuotationMarks,
  TVListing,
  PrintText
} from '@ttab/textbit-plugins'
import { ImageSearchPlugin } from '../../plugins/ImageSearch'
import { FactboxPlugin } from '../../plugins/Factboxes'

import {
  useQuery,
  useLink,
  useRegistry
} from '@/hooks'
import type { ViewMetadata, ViewProps } from '@/types'
import { EditorHeader } from './PrintEditorHeader'
import { Error, Error as ErrorView } from '../Error'
import { Notes } from '@/components/Notes'
import { getValueByYPath } from '@/shared/yUtils'
import { getContentMenuLabels } from '@/defaults/contentMenuLabels'
import { ScrollArea } from '@ttab/elephant-ui'
import { Layouts } from './components/Layouts'
import type * as Y from 'yjs'
import { imageConsume } from './lib/imageConsume'
import { imageConsumes } from './lib/imageConsumes'
import { getImageSrc } from './lib/getImageSrc'
import { ChannelComboBox } from './components/ChannelComboBox'
import { useYDocument, useYValue, type YDocument } from '@/modules/yjs/hooks'
import { View } from '@/components/View'
import { BaseEditor } from '@/components/Editor/BaseEditor'
import { useTranslation } from 'react-i18next'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'

const meta: ViewMetadata = {
  name: 'PrintEditor',
  path: `${import.meta.env.BASE_URL || ''}/print-editor`,
  widths: {
    sm: 12,
    md: 12,
    lg: 12,
    xl: 6,
    '2xl': 6,
    hd: 6,
    fhd: 3,
    qhd: 3,
    uhd: 2
  }
}

// Main Editor Component - Handles document initialization
const PrintEditor = (props: ViewProps): JSX.Element | null => {
  const featureFlags = useFeatureFlags(['hasPrint'])
  const [query] = useQuery()
  const documentId = props.id || query.id as string
  const { t } = useTranslation(['print', 'errors'])

  if (!featureFlags.hasPrint) {
    return (
      <Error
        title={t('errors:messages.errorTitle')}
        message={t('errors:messages.unknownErrorAdminInfo')}
      />
    )
  }

  // Error handling for missing document
  if (!documentId || typeof documentId !== 'string') {
    return (
      <ErrorView
        title={t('print:editor.error.missingDocument')}
        message={t('print:editor.error.missingDocumentMessage')}
      />
    )
  }

  return (
    <EditorWrapper documentId={documentId} {...props} />
  )
}

// Main editor wrapper after document initialization
function EditorWrapper(props: ViewProps & {
  documentId: string
  autoFocus?: boolean
}): JSX.Element {
  const ydoc = useYDocument<Y.Map<unknown>>(props.documentId, {
    ignoreChangeKeys: [
      'meta.tt/print-article[0].meta.tt/article-layout[*].data.status',
      'root.title'
    ]
  })
  const [documentLanguage] = getValueByYPath<string>(ydoc.ele, 'root.language')
  const [content] = getValueByYPath<Y.XmlText>(ydoc.ele, 'content', true)

  const { repository } = useRegistry()
  const openFactboxEditor = useLink('Factbox')
  const openImageSearch = useLink('ImageSearch')
  const openFactboxes = useLink('Factboxes')
  const { t, i18n } = useTranslation()

  const activeLanguage = i18n.resolvedLanguage

  // Plugin configuration
  const configuredPlugins = useMemo(() => {
    return [
      Bold(),
      Italic(),
      ImageSearchPlugin({ openImageSearch }),
      FactboxPlugin({ openFactboxes }),
      Table(),
      LocalizedQuotationMarks(),
      PrintText(),
      Text({
        countCharacters: ['heading-1'],
        ...getContentMenuLabels()
      }),
      Image({
        consume: ({ input }: { input: TBResource | TBResource[] }) =>
          imageConsume(input, repository!),
        consumes: imageConsumes,
        removable: true,
        enableCrop: true,
        getImageSrc: (properties: Record<string, unknown>) =>
          getImageSrc(properties, repository!),
        captionLabel: t('editor:image.captionLabel'),
        bylineLabel: t('editor:image.bylineLabel')
      }),
      TVListing({
        channelComponent: () => ChannelComboBox()
      }),
      TTVisual({
        captionLabel: t('editor:image.captionLabel'),
        bylineLabel: t('editor:image.bylineLabel'),
        enableCrop: true
      }),
      Factbox({
        headerTitle: t('editor:factbox.headerTitle'),
        modifiedLabel: t('editor:factbox.modifiedLabel'),
        createdLabel: t('editor:factbox.createdLabel'),
        lastModifiedLabel: t('editor:factbox.lastModifiedLabel'),
        footerTitle: t('editor:factbox.footerTitle'),
        factboxNewTitle: t('editor:factbox.factboxNewTitle'),
        addSingleLabel: t('editor:factbox.addSingleLabel'),
        onEditOriginal: (id: string) => {
          openFactboxEditor(undefined, { id })
        },
        removable: true,
        locale: activeLanguage,
        ...getContentMenuLabels()
      })
    ]
  }, [openFactboxEditor, repository, openFactboxes, openImageSearch, t, activeLanguage])

  if (!content) {
    return <View.Root />
  }

  return (
    <View.Root>
      <BaseEditor.Root
        ydoc={ydoc}
        content={content}
        plugins={configuredPlugins}
        lang={documentLanguage}
      >
        <EditorContainer ydoc={ydoc} />
      </BaseEditor.Root>
    </View.Root>
  )
}


// Container component that uses TextBit context
function EditorContainer({ ydoc }: {
  ydoc: YDocument<Y.Map<unknown>>
}): JSX.Element {
  const [flowName] = useYValue<string>(ydoc.ele, 'links.tt/print-flow[0].title')

  return (
    <>
      <EditorHeader ydoc={ydoc} flowName={flowName} />

      <Notes ydoc={ydoc} />
      <View.Content className='flex flex-col w-full max-w-[1400px] grow'>
        <section className='flex flex-row items-start gap-8 @4xl/view:grid @4xl/view:grid-cols-12 @4xl/view:gap-6'>
          <div className='flex-1 min-w-0 @4xl/view:col-span-8'>
            <ScrollArea className='h-full @4xl/view:h-[calc(100vh-7rem)]'>
              <div className='flex-grow overflow-auto pr-12 max-w-screen-2xl'>
                {!!ydoc.provider && ydoc.provider.isSynced
                  ? <BaseEditor.Text ydoc={ydoc} autoFocus={true} />
                  : null}
              </div>
            </ScrollArea>
          </div>
          <Layouts
            ydoc={ydoc}
          />

        </section>

      </View.Content>

      <View.Footer>
        {/*
          No `lang` prop: the print editor shows no language chip, unlike the
          article editor.
        */}
        <BaseEditor.Footer />
      </View.Footer>
    </>
  )
}

PrintEditor.meta = meta

export { PrintEditor }
